import { getResultComment } from '@/pages/tmt/tmtConfig';

interface ResultScreenProps {
  score: number;
  totalTimeMs: number;
  wrongCount: number;
  onRetry: () => void;
  onNext: () => void;
}

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function ResultScreen({
  score,
  totalTimeMs,
  wrongCount,
  onRetry,
  onNext,
}: ResultScreenProps) {
  const comment = getResultComment(score);
  const reachedText = `완료 시간 ${formatTime(totalTimeMs)} · 오답 ${wrongCount}회`;

  return (
    <div className="flex flex-col h-full bg-secondary-100 animate-fade-in-up">
      {/* 상단 헤더 */}
      <div className="h-14 flex items-center justify-center px-4 shrink-0">
        <h1 className="text-base font-bold text-foreground-900 leading-none">몸풀기 완료</h1>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="flex-1 flex flex-col px-6 py-6">
        {/* 흰색 콘텐츠 패널 */}
        <div className="flex-1 flex flex-col bg-white rounded-2xl overflow-hidden">
          {/* 몸풀기 결과 영역 */}
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-8">
            <p className="text-[34px] font-bold text-foreground-900 leading-none">{score}점</p>
            <p className="text-lg font-semibold text-foreground-900 leading-snug mt-3">{comment}</p>
            <p className="text-sm text-foreground-600 leading-snug mt-1.5">{reachedText}</p>

            {/* 미반영 안내 */}
            <div className="mt-6 border border-background-300 rounded-lg px-3 py-2.5 flex items-start gap-2 max-w-[300px]">
              <i className="ri-information-line text-foreground-500 text-base leading-none mt-[1px] shrink-0" />
              <p className="text-[13px] leading-relaxed text-left text-foreground-600">
                몸풀기 결과는{' '}
                <span className="font-bold text-primary-800">인지건강 체크 결과에 반영되지 않아요</span>.
              </p>
            </div>
          </div>

          {/* 옅은 구분선 */}
          <div className="h-px bg-background-200 shrink-0" />

          {/* 다음 단계 안내 영역 */}
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-8">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-voiceprint-line text-2xl leading-none" />
            </div>
            <p className="text-xl font-bold text-foreground-900 leading-snug mt-4">
              다음은 음성으로 진행하는
              <br />
              인지건강 체크입니다.
            </p>
          </div>
        </div>
      </div>

      {/* 하단 단계 이동 버튼 */}
      <div className="w-full px-6 pt-2 pb-6 flex gap-3 shrink-0">
        <button
          type="button"
          onClick={onRetry}
          className="h-14 flex-[2] flex items-center justify-center gap-1.5 rounded-2xl bg-white border border-background-300 text-foreground-700
            font-semibold text-base whitespace-nowrap
            active:scale-[0.98] transition-transform duration-150
            hover:bg-background-100"
        >
          <i className="ri-arrow-left-line text-base leading-none" />
          다시하기
        </button>
        <button
          type="button"
          onClick={onNext}
          className="h-14 flex-[3] flex items-center justify-center gap-1.5 rounded-2xl bg-primary-800 text-white
            font-semibold text-base whitespace-nowrap
            active:scale-[0.98] transition-transform duration-150
            hover:bg-primary-900"
        >
          다음 단계로
          <i className="ri-arrow-right-line text-base leading-none" />
        </button>
      </div>
    </div>
  );
}